import { Component, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExamenesService, Examen, Pregunta, Intento, RespuestaIntento } from '../services/examenes.service';
import { NotificationService } from '../services/notification.service';
import { interval, Subscription } from 'rxjs';

@Component({
  selector: 'app-exam-new',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './exam-new.html',
  styleUrl: './exam-new.css',
})
export class ExamNewComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private examenesService = inject(ExamenesService);
  private notificationService = inject(NotificationService);

  // Estado de carga
  isLoading = signal(false);
  
  // Datos del examen
  examen = signal<Examen | null>(null);
  examenOriginal = signal<Examen | null>(null); // Para comparar
  intento = signal<Intento | null>(null);
  
  // Navegación de preguntas
  currentQuestionIndex = signal(0);
  respuestas = new Map<number, RespuestaIntento>();
  
  // Temporizador
  tiempoRestante = signal<number>(0);
  tiempoTranscurrido = signal<number>(0);
  private timerSubscription?: Subscription;
  
  // Estado del examen
  examenIniciado = signal(false);
  examenFinalizado = signal(false);
  mostrandoResultados = signal(false);
  resultados = signal<any>(null);
  
  // Intentos
  intentosRealizados = signal(0);
  intentosDisponibles = signal(true);
  intentoEnProgreso = signal<Intento | null>(null);
  todosLosIntentos = signal<any[]>([]);
  mostrarHistorialIntentos = signal(false);
  
  // UI
  showConfirmDialog = signal(false);
  showTimeWarning = signal(false);
  
  // Debug info
  mostrarDebugInfo = signal(false);
  duplicadosDetectados = signal<any>(null);
  
  // Query params
  private courseId: number | null = null;
  private examId: number | null = null;

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.examId = +params['id'];
      
      this.route.queryParams.subscribe(queryParams => {
        this.courseId = queryParams['course'] ? +queryParams['course'] : null;
        
        if (this.examId) {
          this.cargarExamen(this.examId);
        }
      });
    });
  }

  ngOnDestroy() {
    this.detenerTemporizador();
  }

  /**
   * Carga los datos del examen y verifica intentos previos
   */
  cargarExamen(idExamen: number) {
    this.isLoading.set(true);
    
    // Primero obtener información de intentos previos
    this.examenesService.getMisIntentos(idExamen).subscribe({
      next: (intentosResponse) => {
        // Cargar datos del examen
        this.examenesService.getExamen(idExamen).subscribe({
          next: (response) => {
            this.isLoading.set(false);
            
            if (response.ok && response.data) {
              // Guardar copia original para comparación
              this.examenOriginal.set(JSON.parse(JSON.stringify(response.data)));
              
              const examenRecibido = response.data;
              console.log(`🔍 [EXAM-NEW] Examen recibido del backend - ID: ${examenRecibido.id_examen}`);
              console.log(`📊 [EXAM-NEW] Total de preguntas recibidas: ${examenRecibido.preguntas?.length || 0}`);
              
              // Detectar y limpiar duplicados ANTES de establecer el examen
              if (examenRecibido.preguntas && examenRecibido.preguntas.length > 0) {
                const resultado = this.analizarYLimpiarDuplicados(examenRecibido.preguntas);
                
                if (resultado.duplicadosEncontrados > 0) {
                  console.error(`🚨 [EXAM-NEW] Se detectaron ${resultado.duplicadosEncontrados} preguntas duplicadas`);
                  console.error(`🚨 [EXAM-NEW] IDs duplicados:`, resultado.idsDuplicados);
                  
                  this.duplicadosDetectados.set({
                    total: resultado.total,
                    unicos: resultado.unicos,
                    duplicados: resultado.duplicadosEncontrados,
                    ids: resultado.idsDuplicados,
                    detalles: resultado.detalles
                  });
                  
                  this.notificationService.warning(
                    'Preguntas validadas',
                    `Se detectaron y corrigieron ${resultado.duplicadosEncontrados} pregunta(s) duplicada(s).`
                  );
                }
                
                examenRecibido.preguntas = resultado.preguntasLimpias;
              }
              
              this.examen.set(examenRecibido);
              
              // Verificar intentos disponibles
              if (intentosResponse.ok && intentosResponse.data) {
                const intentos = intentosResponse.data;
                this.intentosRealizados.set(intentos.length);
                this.todosLosIntentos.set(intentos);
                
                // Verificar si hay un intento en progreso
                const intentoActivo = intentos.find((i: any) => i.estado === 'En_Progreso');
                if (intentoActivo) {
                  this.intentoEnProgreso.set(intentoActivo);
                  this.notificationService.warning(
                    'Intento en progreso',
                    'Tienes un intento sin finalizar. Puedes continuar donde lo dejaste.'
                  );
                }
                
                // Verificar si quedan intentos disponibles
                const examen = examenRecibido;
                if (examen.intentos_permitidos > 0 && intentos.length >= examen.intentos_permitidos) {
                  this.intentosDisponibles.set(false);
                  this.mostrarHistorialIntentos.set(true);
                  
                  this.notificationService.info(
                    'Intentos agotados',
                    `Has utilizado todos tus intentos disponibles (${examen.intentos_permitidos})`
                  );
                }
              }
            } else {
              this.notificationService.error(
                'Error',
                response.msg || 'No se pudo cargar el examen'
              );
              this.goBack();
            }
          },
          error: (error) => {
            this.isLoading.set(false);
            console.error('Error al cargar examen:', error);
            this.notificationService.error(
              'Error de Conexión',
              'No se pudo conectar con el servidor'
            );
            this.goBack();
          }
        });
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al verificar intentos:', error);
      }
    });
  }

  /**
   * Analiza y limpia preguntas duplicadas
   */
  private analizarYLimpiarDuplicados(preguntas: Pregunta[]): {
    preguntasLimpias: Pregunta[];
    total: number;
    unicos: number;
    duplicadosEncontrados: number;
    idsDuplicados: number[];
    detalles: any[];
  } {
    if (!preguntas || preguntas.length === 0) {
      return {
        preguntasLimpias: [],
        total: 0,
        unicos: 0,
        duplicadosEncontrados: 0,
        idsDuplicados: [],
        detalles: []
      };
    }
    
    const idsVistos = new Set<number>();
    const preguntasLimpias: Pregunta[] = [];
    const duplicadosInfo: any[] = [];
    const idsDuplicados: number[] = [];
    
    preguntas.forEach((pregunta, indice) => {
      if (!idsVistos.has(pregunta.id_pregunta)) {
        idsVistos.add(pregunta.id_pregunta);
        preguntasLimpias.push(pregunta);
      } else {
        idsDuplicados.push(pregunta.id_pregunta);
        duplicadosInfo.push({
          id: pregunta.id_pregunta,
          indice: indice,
          texto: pregunta.texto_pregunta.substring(0, 80) + '...'
        });
      }
    });
    
    return {
      preguntasLimpias,
      total: preguntas.length,
      unicos: preguntasLimpias.length,
      duplicadosEncontrados: duplicadosInfo.length,
      idsDuplicados: [...new Set(idsDuplicados)],
      detalles: duplicadosInfo
    };
  }

  /**
   * Inicia el intento de examen
   */
  iniciarExamen() {
    if (!this.examId) return;
    
    if (!this.intentosDisponibles()) {
      this.notificationService.error(
        'Sin intentos',
        'Ya no tienes intentos disponibles para este examen'
      );
      return;
    }
    
    this.isLoading.set(true);
    
    this.examenesService.iniciarIntento(this.examId).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok && response.data) {
          const data = response.data as any;
          
          if (data.intento) {
            this.intento.set(data.intento);
            
            const esIntentoNuevo = !response.msg?.includes('en progreso');
            
            // Cargar las preguntas del intento
            if (data.preguntas && data.preguntas.length > 0) {
              const examen = this.examen();
              if (examen) {
                console.log(`🔍 [INTENTO] Preguntas recibidas del intento: ${data.preguntas.length}`);
                
                // SIEMPRE LIMPIAR DUPLICADOS
                const resultado = this.analizarYLimpiarDuplicados(data.preguntas);
                
                if (resultado.duplicadosEncontrados > 0) {
                  console.error(`🚨 [INTENTO] Duplicados detectados: ${resultado.duplicadosEncontrados}`);
                }
                
                // Actualizar el examen con las preguntas limpias
                this.examen.set({
                  ...examen,
                  preguntas: resultado.preguntasLimpias
                });
                
                // APLICAR ALEATORIZACIÓN SOLO EN INTENTOS NUEVOS
                if (esIntentoNuevo) {
                  if (examen.aleatorizar_preguntas) {
                    console.log('🔀 Aleatorizando preguntas...');
                    this.aleatorizarPreguntas();
                  }
                  
                  if (examen.aleatorizar_opciones) {
                    console.log('🔀 Aleatorizando opciones...');
                    this.aleatorizarOpciones();
                  }
                }
              }
            }
            
            this.examenIniciado.set(true);
            
            // Calcular tiempo restante
            const intento = data.intento;
            const examen = this.examen();
            
            if (examen && examen.tiempo_limite && examen.tiempo_limite > 0) {
              const fechaInicio = new Date(intento.fecha_inicio).getTime();
              const ahora = new Date().getTime();
              const tiempoTranscurridoSeg = Math.floor((ahora - fechaInicio) / 1000);
              const tiempoLimiteSeg = examen.tiempo_limite * 60;
              const tiempoRestanteSeg = Math.max(0, tiempoLimiteSeg - tiempoTranscurridoSeg);
              
              this.tiempoRestante.set(tiempoRestanteSeg);
              this.tiempoTranscurrido.set(tiempoTranscurridoSeg);
              
              if (tiempoRestanteSeg > 0) {
                this.iniciarTemporizador();
              } else {
                this.notificationService.error('Tiempo agotado', 'El tiempo del examen ha finalizado');
                this.finalizarExamen();
                return;
              }
            }
            
            if (response.msg?.includes('en progreso')) {
              this.notificationService.info(
                'Continuar examen',
                'Continuando con tu intento anterior'
              );
            } else {
              this.notificationService.success(
                '¡Examen iniciado!',
                'Tiempo: ' + (examen?.tiempo_limite ? examen.tiempo_limite + ' minutos' : 'ilimitado')
              );
            }
          }
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo iniciar el examen'
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al iniciar examen:', error);
        this.notificationService.error(
          'Error',
          error.error?.msg || 'No se pudo iniciar el examen'
        );
      }
    });
  }

  /**
   * Guarda la respuesta de una pregunta
   */
  guardarRespuesta(pregunta: Pregunta, idOpcion?: number, respuestaTexto?: string) {
    if (!this.intento()) return;
    
    const respuesta: RespuestaIntento = {
      id_pregunta: pregunta.id_pregunta,
      id_opcion: idOpcion,
      respuesta_texto: respuestaTexto,
      tiempo_respuesta: this.tiempoTranscurrido()
    };
    
    this.respuestas.set(pregunta.id_pregunta, respuesta);
    
    this.examenesService.responderPregunta(this.intento()!.id_intento, respuesta).subscribe({
      next: (response) => {
        if (response.ok) {
          console.log('✅ Respuesta guardada:', pregunta.id_pregunta);
        }
      },
      error: (error) => {
        console.error('Error al guardar respuesta:', error);
      }
    });
  }

  /**
   * Navega entre preguntas
   */
  siguientePregunta() {
    const examen = this.examen();
    if (!examen) return;
    
    if (this.currentQuestionIndex() < examen.preguntas.length - 1) {
      this.currentQuestionIndex.update(i => i + 1);
    }
  }

  anteriorPregunta() {
    if (this.currentQuestionIndex() > 0) {
      this.currentQuestionIndex.update(i => i - 1);
    }
  }

  irAPregunta(index: number) {
    const examen = this.examen();
    if (!examen) return;
    
    if (index >= 0 && index < examen.preguntas.length) {
      this.currentQuestionIndex.set(index);
    }
  }

  /**
   * Confirmación y finalización del examen
   */
  confirmarFinalizarExamen() {
    const examen = this.examen();
    if (!examen) return;
    
    const respondidas = this.respuestas.size;
    const totales = examen.preguntas.length;
    
    if (respondidas < totales) {
      const pendientes = totales - respondidas;
      this.notificationService.warning(
        'Preguntas sin responder',
        `Tienes ${pendientes} pregunta(s) sin responder.`
      );
    }
    
    this.showConfirmDialog.set(true);
  }

  finalizarExamen() {
    if (!this.intento()) return;
    
    this.showConfirmDialog.set(false);
    this.isLoading.set(true);
    this.detenerTemporizador();
    
    this.examenesService.finalizarIntento(this.intento()!.id_intento).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        
        if (response.ok) {
          this.examenFinalizado.set(true);
          
          const examen = this.examen();
          if (examen?.mostrar_resultados_inmediatos) {
            this.cargarResultados();
          } else {
            this.notificationService.success(
              '¡Examen finalizado!',
              'Tu examen ha sido enviado para revisión'
            );
            setTimeout(() => this.goBack(), 2000);
          }
        } else {
          this.notificationService.error(
            'Error',
            response.msg || 'No se pudo finalizar el examen'
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        console.error('Error al finalizar examen:', error);
        this.notificationService.error(
          'Error',
          'No se pudo finalizar el examen'
        );
      }
    });
  }

  cargarResultados() {
    if (!this.intento()) return;
    
    this.examenesService.getResultados(this.intento()!.id_intento).subscribe({
      next: (response) => {
        if (response.ok && response.data) {
          this.resultados.set(response.data);
          this.mostrandoResultados.set(true);
        }
      },
      error: (error) => {
        console.error('Error al cargar resultados:', error);
      }
    });
  }

  cancelarFinalizar() {
    this.showConfirmDialog.set(false);
  }

  /**
   * Temporizador
   */
  private iniciarTemporizador() {
    this.timerSubscription = interval(1000).subscribe(() => {
      this.tiempoRestante.update(t => t - 1);
      this.tiempoTranscurrido.update(t => t + 1);
      
      if (this.tiempoRestante() === 300 && !this.showTimeWarning()) {
        this.showTimeWarning.set(true);
        this.notificationService.warning(
          '⏰ Tiempo',
          'Te quedan 5 minutos'
        );
      }
      
      if (this.tiempoRestante() <= 0) {
        this.detenerTemporizador();
        this.notificationService.error(
          'Tiempo agotado',
          'El tiempo del examen ha finalizado'
        );
        this.finalizarExamen();
      }
    });
  }

  private detenerTemporizador() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  /**
   * Aleatorización
   */
  private aleatorizarPreguntas() {
    const examen = this.examen();
    if (!examen) return;
    
    const preguntas = [...examen.preguntas];
    
    for (let i = preguntas.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [preguntas[i], preguntas[j]] = [preguntas[j], preguntas[i]];
    }
    
    this.examen.set({ ...examen, preguntas });
  }

  private aleatorizarOpciones() {
    const examen = this.examen();
    if (!examen) return;
    
    const preguntasActualizadas = examen.preguntas.map(pregunta => {
      if (!pregunta.opciones || pregunta.opciones.length === 0) {
        return pregunta;
      }
      
      if (pregunta.tipo_pregunta === 'respuesta_corta' || pregunta.tipo_pregunta === 'respuesta_larga') {
        return pregunta;
      }
      
      const opciones = [...pregunta.opciones];
      
      for (let i = opciones.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [opciones[i], opciones[j]] = [opciones[j], opciones[i]];
      }
      
      return { ...pregunta, opciones };
    });
    
    this.examen.set({ ...examen, preguntas: preguntasActualizadas });
  }

  /**
   * Utilidades
   */
  formatTime(segundos: number): string {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  isPreguntaRespondida(idPregunta: number): boolean {
    return this.respuestas.has(idPregunta);
  }

  getRespuesta(idPregunta: number): RespuestaIntento | undefined {
    return this.respuestas.get(idPregunta);
  }

  goBack() {
    if (this.courseId) {
      this.router.navigate(['/course', this.courseId, 'watch']);
    } else {
      this.router.navigate(['/dashboard/home']);
    }
  }

  formatAttemptDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-SV', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatAttemptTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs} segundos`;
    }
    return `${mins} min ${secs} seg`;
  }

  getBestAttempt(): any | null {
    const intentos = this.todosLosIntentos();
    if (intentos.length === 0) return null;
    
    return intentos.reduce((best, current) => {
      const bestScore = parseFloat(best.porcentaje || '0');
      const currentScore = parseFloat(current.porcentaje || '0');
      return currentScore > bestScore ? current : best;
    });
  }

  toggleDebugInfo() {
    this.mostrarDebugInfo.update(v => !v);
  }
}

